import { h, Component, Prop, Element, State, Host } from '@stencil/core';
import { BrightcovePlayerService, BRIGHTCOVE } from '../../utils/BrightcoveService.js';

type VideoTypeProp = 'data-video-id' | 'data-playlist-id';

declare global {
  interface Window {
    PS: any;
    bc: any;
    videojs: any;
  }
}
const PS = window.PS;

// Global event bus for video coordination
const videoEventBus = {
  playing: null as string | null,
  listeners: new Set<(videoId: string) => void>(),
  
  notifyPlaying(videoId: string) {
    this.playing = videoId;
    this.listeners.forEach(listener => listener(videoId));
  },
  
  subscribe(listener: (videoId: string) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
};

@Component({
  shadow: false,
  styleUrl: 'igws-video.less',
  tag: 'igws-video',
})
export class VideoComponent {
  @Element() el!: HTMLIgwsVideoElement;

  /** The ID of the video. Can be overwritten by `videoReference` property. */
  @Prop() videoId: string = '';

  /** The reference of the video. Can overwrite the `videoId`. */
  @Prop() videoReference: string = '';

  /** Comes from AEM `Player` option. Is attached to request url. Default: `2834081556001` (single player). */
  @Prop() playerType: string = BRIGHTCOVE.AEM.SINGLE_PLAYER_ID;

  /** Account ID attached to api request. Default: `2830693144001`. */
  @Prop() accountId: string = BRIGHTCOVE.ACCOUNT.ID;

  /** Will initialize player when `true`. Default: `false`. */
  @Prop() isLive: boolean = false;

  /** Will initialize and play video when `true`. Default: `false`. */
  @Prop() autoStart: boolean = false;

  /** Overlay property. Will display overlay with `replay button` when video ends.  Default: `false` */
  @Prop() showoverlay: boolean = false;

  /** Overlay content. Will display with additional text in overlay. */
  @Prop() overlayText: string = '';

  /** TODO */
  @Prop() beforeLiveText: string = '';

  /** Overlay content. Will display additional link button. */
  @Prop() ctaText: string = '';

  /** Link for `ctaText` link button. */
  @Prop() ctaUrl: string = '';

  /** Overlay content. Will display additional secondary link button. */
  @Prop() ctaTextSecondary: string = '';

  /** Link for `ctaTextSecondary` link button. */
  @Prop() ctaUrlSecondary: string = '';

  /** Shows the description if it exists in the video mediainfo. https://player.support.brightcove.com/references/video-metadata-mediainfo.html */
  @Prop() showDescription: boolean = false;

  @State() isOverlayVisible: boolean = false;
  @State() isPosterImageVisible: boolean = true;
  @State() videoMediaInfo: any;


  private hasTranscriptSlot: boolean = false;
  private hasImgSlot: boolean = false;
  private playerTypeToDownload: Map<any, any> = new Map();
  private playerService: BrightcovePlayerService = new BrightcovePlayerService();
  private bctid = new URLSearchParams(window.location.search).get('bctid');
  private bclid = new URLSearchParams(window.location.search).get('bclid');
  private playerInitialized: boolean = false;
  private unsubscribeFromVideoBus?: () => void;

  get shouldRenderPlaylist() {
    return [BRIGHTCOVE.AEM.PLAYLIST_BELOW_ID, BRIGHTCOVE.AEM.PLAYLIST_RIGHT_ID].includes(this.playerType);
  }

  get shouldGetVideoIdFromUrl() {
    return this.bctid && this.bclid && this.playerType !== BRIGHTCOVE.AEM.SINGLE_PLAYER_ID;
  }

  get computedPlayerId(): string {
    return this.playerTypeToDownload.get(this.playerType);
  }

  get computedVideoTypeProperty(): VideoTypeProp {
    return this.playerType === BRIGHTCOVE.AEM.SINGLE_PLAYER_ID ? 'data-video-id' : 'data-playlist-id';
  }

  get computedVideoId(): string {
    if (this.shouldGetVideoIdFromUrl) {
      return this.bclid || '';
    }
    return this.videoReference ? `ref:${this.videoReference}` : this.videoId;
  }

  get computedHookId(): string {
    if (this.shouldGetVideoIdFromUrl) {
      return `id-${this.bctid}-video`;
    }
    return this.videoReference ? `id-${this.videoReference}-video` : `id-${this.videoId}-video`;
  }

  get brightcovePlayerUrl(): string {
    return `https://players.brightcove.net/${this.accountId}/${this.computedPlayerId}_default/index.min.js`;
  }

  disconnectedCallback() {
    if (this.playerService && this.playerService.player) {
      try {
        this.playerService.player.dispose();
      } catch (e) {
        console.error('Error disposing player:', e);
      }
    }
    
    // Unsubscribe from video events
    if (this.unsubscribeFromVideoBus) {
      this.unsubscribeFromVideoBus();
    }
  }

  componentWillLoad() {
    // Check for slots
    this.hasTranscriptSlot = !!this.el.querySelector('[slot="transcript"]');
    this.hasImgSlot = !!this.el.querySelector('[slot="img"]');
    
    // Match id from AEM with player id from brightcove
    this.playerTypeToDownload.set(BRIGHTCOVE.AEM.SINGLE_PLAYER_ID, BRIGHTCOVE.SCRIPT.DEFAULT_ID);
    this.playerTypeToDownload.set(BRIGHTCOVE.AEM.PLAYLIST_BELOW_ID, BRIGHTCOVE.SCRIPT.PLAYLIST_BELOW_ID);
    this.playerTypeToDownload.set(BRIGHTCOVE.AEM.PLAYLIST_RIGHT_ID, BRIGHTCOVE.SCRIPT.PLAYLIST_RIGHT_ID);
    this.playerTypeToDownload.set(BRIGHTCOVE.AEM.LIVE_PLAYER_ID, BRIGHTCOVE.SCRIPT.DEFAULT_ID);
    
    // Subscribe to video events
    this.unsubscribeFromVideoBus = videoEventBus.subscribe((playingVideoId) => {
      // If another video is playing and we have a player, pause it
      if (playingVideoId !== this.computedHookId && this.playerService.player) {
        this.playerService.pauseVideo();
      }
    });
  }

  async componentDidLoad() {
    if (this.hasTranscriptSlot) this.handleTranscriptToggle();
    
    // Download the script
    await this.playerService.downloadBrightcoveScript(this.brightcovePlayerUrl);
    
    // Small delay to ensure script is ready
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Only auto-initialize if autoStart is true
    if (this.autoStart) {
      await this.initializeAndPlay();
    } else if (this.isLive) {
      await this.initializePlayer();
    } else {
      // For regular videos, initialize but ensure they don't autoplay
      await this.initializePlayer();
      // Immediately pause after initialization
      setTimeout(() => {
        if (this.playerService.player) {
          this.playerService.player.pause();
        }
      }, 500);
    }
  }

  private replay(e: UIEvent): void {
    e.preventDefault();
    this.isOverlayVisible = false;
    this.playerService.replayVideo();
  }

  private makeOverlayActive(): void {
    if (this.showoverlay) {
      this.isOverlayVisible = true;
    }
  }

  private handleTranscriptToggle() {
    const link = this.el.querySelector('.igws-video-transcript a');
    if (!link) return;
    const content = link.nextElementSibling;
    link.addEventListener('click', e => {
      e.preventDefault();
      if (content) content.classList.toggle('show');
    });
  }

  private async handlePosterClickEvent() {
    this.isPosterImageVisible = false;
    await this.initializeAndPlay();
  }

  private registerGaTrackingPlayerEvents() {
    if (PS?.GAtracking?.TrackWebinarPlayer) {
      this.playerService.onVideo('play', () => {
        PS.GAtracking.TrackWebinarPlayer.getVideoData(this.playerService.player, 'play');
      });
      this.playerService.onVideo('pause', () => {
        PS.GAtracking.TrackWebinarPlayer.getVideoData(this.playerService.player, 'pause');
      });
      this.playerService.onVideo('timeupdate', () => {
        PS.GAtracking.TrackWebinarPlayer.getVideoData(this.playerService.player, 'percentile');
      });
      this.playerService.onVideo('ended', () => {
        PS.GAtracking.TrackWebinarPlayer.getVideoData(this.playerService.player, 'video complete');
      });
    }
  }

  private registerPlayerEvents() {
    this.playerService.onVideo('ended', () => this.makeOverlayActive());
    this.playerService.onVideo('play', () => {
      this.isPosterImageVisible = false;
      // Notify other videos that this one is playing
      videoEventBus.notifyPlaying(this.computedHookId);
    });
    this.playerService.onVideo('loadstart', (video: any) => (this.videoMediaInfo = video.target?.player?.mediainfo));
  }

  private async initializePlayer() {
    if (this.playerInitialized || !window.videojs) return;

    // Initialize this specific player
    this.playerService.initializeSpecificPlayer(this.computedHookId);
    
    if (this.playerService.player) {
      // Ensure the player doesn't autoplay
      this.playerService.player.ready(() => {
        this.playerService.player.autoplay(false);
        this.playerService.player.preload('none');
        if (!this.autoStart) {
          this.playerService.player.pause();
        }
      });
      
      this.registerPlayerEvents();
      this.registerGaTrackingPlayerEvents();
      this.playerInitialized = true;
    }
  }

  private async initializeAndPlay(isMuted?: boolean) {
    await this.initializePlayer();
    
    // Small delay to ensure player is ready
    await new Promise(resolve => setTimeout(resolve, 100));
    
    if (this.playerService.player) {
      this.playerService.playVideo(isMuted);
    }
  }

  private brighcovePlayerTemplate(): any {
    const props = {
      id: this.computedHookId,
      [this.computedVideoTypeProperty]: this.computedVideoId,
      'data-account': this.accountId,
      'data-player': this.computedPlayerId,
      'data-embed': 'default',
      'data-setup': JSON.stringify({
        autoplay: false,
        preload: 'none'
      }),
      class: 'video-js',
      controls: true,
      preload: 'none',
      autoplay: 'false',
      muted: false,
      playsinline: true,
    };

    return <video {...props}></video>;
  }

  private playlistTemplate() {
    return (
      <div class={`igws-video__playlist-wrapper show`}>
        <div data-for={this.computedHookId} class="vjs-playlist vjs-playlist-horizontal"></div>
      </div>
    );
  }

  private overlayTemplate() {
    const beforeLiveText = (
      <p class="igws-video__overlay-text igws-video__overlay-text--beforelive">{this.beforeLiveText}</p>
    );
    const overlayText = <p class="igws-video__overlay-text">{this.overlayText}</p>;
    const ctaText = (
      <a class="igws-video__cta-button igws-video__cta-button--primary" href={this.ctaUrl}>
        {this.ctaText}
      </a>
    );
    const ctaTextSecondary = (
      <a class="igws-video__cta-button igws-video__cta-button--secondary" href={this.ctaUrlSecondary}>
        {this.ctaTextSecondary}
      </a>
    );

    const replayButton = (
      <a onClick={e => this.replay(e)} class="igws-video__replay-video" href="#">
        Replay Video
      </a>
    );

    return (
      <div class={`igws-video__overlay ${this.isOverlayVisible ? 'active' : ''}`}>
        {this.beforeLiveText && this.isLive ? beforeLiveText : null}
        {this.overlayText && !this.isLive ? overlayText : null}
        {this.ctaText || this.ctaTextSecondary ? (
          <div>
            {this.ctaText ? ctaText : null}
            {this.ctaTextSecondary ? ctaTextSecondary : null}
          </div>
        ) : null}
        {!this.isLive ? replayButton : null}
      </div>
    );
  }

  private descriptionTemplate() {
    return (
      <div class="igws-video-description">
        <p class="igws-video-description_content">{this.videoMediaInfo?.description}</p>
      </div>
    );
  }

  render() {
    return (
      <Host>
        <div class="igws-video">
          <div class="igws-video__brightcove-player">
            {this.brighcovePlayerTemplate()}
            {this.shouldRenderPlaylist ? this.playlistTemplate() : null}
          </div>
          {this.showoverlay ? this.overlayTemplate() : null}
          <div
            class={`igws-video__poster-img-slot ${this.isPosterImageVisible ? '' : 'hide'}`}
            onClick={() => this.handlePosterClickEvent()}
          >
            <slot name="img"></slot>
          </div>
        </div>
        {this.showDescription ? this.descriptionTemplate() : null}
        <div class="igws-video-transcript">
          <slot name="transcript"></slot>
        </div>
      </Host>
    );
  }
}